import { useLoan } from "@/context/LoanContext";
import { LifecycleState, TabKey } from "@/lib/lifecycle";
import { useVisibleTabs } from "@/hooks/useVisibleTabs";
import { useFeatureFlag, useFeatureFlagsLoading } from "@/hooks/useFeatureFlag";
import type { FeatureFlagName } from "@/lib/featureFlags";
import { cn } from "@/lib/utils";
import { Navigate } from "react-router-dom";

/**
 * NP-350 — closes the calculator's deep links while Legal reviews its content.
 *
 * The calculator is not a tab, so RequireTab's list cannot cover it; this is the
 * same shape for the one flag. AC-4 allows a redirect or a 404 and this redirects:
 * every calculator path sits behind ProtectedRoute, so a signed-in borrower who
 * bookmarked /calculator is better served by their dashboard than a dead end. The
 * paths stay in App's KNOWN_APP_PATHS for that reason — dropping them there (the
 * treatment /tasks gets) would turn them into the public 404 instead.
 *
 * `children` is passed as an element rather than gated inside <Calculator> so the
 * page's lazy chunk is never fetched while the flag is off: creating the element
 * does not load it, only rendering does, and the off branch never renders.
 */
export const RequireCalculator = ({ children }: { children: React.ReactNode }) => (
  <RequireFlag flag="DashboardCalculator">{children}</RequireFlag>
);

/**
 * NP-754: Payments needs the same /dashboard redirect for /payments. RequireTab
 * cannot give it — it sends hidden tabs to "/" — so the calculator's guard takes
 * the flag as a prop and serves both.
 */
export const RequireFlag = ({ flag, children }: { flag: FeatureFlagName; children: React.ReactNode }) => {
  const enabled = useFeatureFlag(flag);
  const flagsLoading = useFeatureFlagsLoading();
  // Copilot review (PR #342): the provider seeds from INITIAL_FLAGS — where this
  // flag is off — and merges the server's answer only once the fetch lands. On a
  // hard load of /calculator the redirect would therefore fire before the flag that
  // permits the page arrives, so the day Legal clears the calculator every deep link
  // would still bounce to the dashboard. Same shape as RequireTab's NP-72 fix below:
  // hold while the read is in flight, decide once it settles. Redirecting is the
  // irreversible half, which is why it waits and a plain <FeatureGate> need not.
  if (flagsLoading) {
    // role/label per Copilot review (PR #342): the spin is purely visual, so without
    // them a screen reader announces nothing at all during the wait.
    return (
      <div className="flex items-center justify-center min-h-[50vh]" role="status" aria-label="Loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

export const RequireTab = ({ tab, children }: { tab: TabKey; children: React.ReactNode }) => {
  const { state, isLoading } = useLoan();
  // NP-408: flag-aware — deep links to hidden tabs (/income, /conditions)
  // redirect home while the tabs are disabled for v1.0.
  const tabs = useVisibleTabs(state);
  // NP-540: for PROSPECTS the tab list governs the NAV BAR, not reachability.
  // NP-499 hides the Application tab from borrowers with no application — but
  // they are precisely the people who need this route, and the Dashboard's own
  // "Buy a home" card sends them here. Gating it bounced them back, and evicted
  // anyone mid-wizard the moment the lifecycle read NO_APP (a loan read still in
  // flight is enough).
  //
  // Scoped to PROSPECT (Copilot): ACTIVE_* is already redirected upstream by
  // ApplicationRouteGuard, but POST_* is not — an exemption for every state would
  // have opened the wizard to post-close borrowers, who cannot apply on this loan.
  if (tab === "application" && state?.startsWith("PROSPECT")) return <>{children}</>;
  if (!tabs.includes(tab)) {
    // NP-72: on a hard load the lifecycle starts from the local-draft fallback
    // (Prospect) until GET /loans/{id} resolves with the Vesta stage — deep
    // links to stage-gated tabs (e.g. /my-home) bounced to "/" before the
    // promoting read landed (QA TC1). Hold on a spinner while loading; only
    // redirect once the lifecycle is settled.
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export const PageHeader = ({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) => (
  <div className="flex items-end justify-between gap-4 mb-6">
    <div>
      <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const StatePill = ({ state }: { state: LifecycleState }) => (
  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">{state}</span>
);

/**
 * NP-582: no longer reserves bottom space for the fixed mobile nav. AppShell's footer
 * sits after every page and carries that clearance once, so a per-page reserve became
 * dead space between the content and the footer links — 176px on the dashboard, which
 * also stacked its own `pb-24`. This reverses NP-438's "clearance is page-owned" model
 * for the nav specifically; AppShell's <main> still keeps only its baseline padding.
 */
export const AppPageContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={cn(className)}>{children}</div>;
